import React from "react";
import {
  BrainCircuit, Users, Clock, Zap, Target, TrendingUp, TrendingDown, Sparkles,
  AlertCircle, Square, EyeOff, Workflow, Rocket, MessageSquare, Check, ChevronRight,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell, AreaChart, Area,
} from "recharts";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../../../contexts/AuthContext";
import { useDashTheme } from "../hooks/useDashTheme";
import { useAiStream } from "../hooks/useAiStream";
import { PageShell } from "../components/PageShell";
import { SectionCard, Badge, Avatar, Button } from "../components/primitives";
import { EmptyState } from "../components/DataTable";
import { DateRangeTool, presetToRange, rangeForPreset } from "../components/DateRange";
import { ToolSelect } from "../components/ToolSelect";
import AILimitModal from "../../../components/AILimitModal/AILimitModal";
import { SERVER_URL } from "../../../utils/serverConfig";

const fmt = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
};

// Severity → color. Red = close to the sun = act now.
const sevColor = (s) => (s >= 85 ? "#ef4444" : s >= 60 ? "#f97316" : s >= 30 ? "#eab308" : "#10b981");
const sevLabel = (s) => (s >= 85 ? "Critical" : s >= 60 ? "High" : s >= 30 ? "Monitor" : "Minor");
const CATEGORY_ICONS = { tracking: EyeOff, workflow: Workflow, collaboration: Users, workload: Clock, process: Target, risk: AlertCircle, growth: Rocket };

// ─────────────────────────────────────────────────────────────────────────────
// SunCore — the futuristic animated sun. Rotating flare ring, counter-rotating
// dashed orbit, drifting surface arcs, pulsing corona and orbiting sparks.
// Shared between the hero (idle state) and the attention map.
// ─────────────────────────────────────────────────────────────────────────────
function SunDefs() {
  return (
    <defs>
      <radialGradient id="cbSunCore" cx="38%" cy="34%" r="72%">
        <stop offset="0%" stopColor="#fde68a" />
        <stop offset="38%" stopColor="#fbbf24" />
        <stop offset="72%" stopColor="#f59e0b" />
        <stop offset="100%" stopColor="#ea580c" />
      </radialGradient>
      <radialGradient id="cbSunGlow" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#f59e0b" stopOpacity="0.5" />
        <stop offset="55%" stopColor="#f97316" stopOpacity="0.16" />
        <stop offset="100%" stopColor="#f97316" stopOpacity="0" />
      </radialGradient>
      <linearGradient id="cbFlare" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#fef3c7" stopOpacity="0.95" />
        <stop offset="60%" stopColor="#fbbf24" stopOpacity="0.35" />
        <stop offset="100%" stopColor="#f97316" stopOpacity="0" />
      </linearGradient>
      <filter id="cbSoft" x="-60%" y="-60%" width="220%" height="220%">
        <feGaussianBlur stdDeviation="2.2" />
      </filter>
    </defs>
  );
}

// SMIL rotation — CSS/framer transforms on SVG groups are unreliable across
// browsers, native animateTransform always works.
function Spin({ c, dur, reverse = false, children }) {
  return (
    <g>
      <animateTransform attributeName="transform" type="rotate"
        from={`${reverse ? 360 : 0} ${c} ${c}`} to={`${reverse ? 0 : 360} ${c} ${c}`}
        dur={`${dur}s`} repeatCount="indefinite" />
      {children}
    </g>
  );
}

function SunCore({ c, coreR = 44, label = ["COMPANY", "CORE"], streaming = false }) {
  return (
    <g>
      {/* corona — breathing glow */}
      <circle cx={c} cy={c} r={coreR * 2.2} fill="url(#cbSunGlow)">
        <animate attributeName="opacity" values={streaming ? "0.55;1;0.55" : "0.7;1;0.7"} dur="2.6s" repeatCount="indefinite" />
        <animate attributeName="r" values={`${coreR * 2.05};${coreR * 2.35};${coreR * 2.05}`} dur="2.6s" repeatCount="indefinite" />
      </circle>

      {/* rotating flare ring */}
      <Spin c={c} dur={14}>
        <circle cx={c} cy={c} r={coreR + 10} fill="none" stroke="url(#cbFlare)" strokeWidth={2.6}
          strokeDasharray="28 54" strokeLinecap="round" opacity={0.9} />
      </Spin>
      {/* counter-rotating fine orbit */}
      <Spin c={c} dur={26} reverse>
        <circle cx={c} cy={c} r={coreR + 19} fill="none" stroke="#f59e0b" strokeWidth={0.9}
          strokeDasharray="2 9" opacity={0.55} />
      </Spin>

      {/* orbiting sparks */}
      {[{ r: coreR + 10, dur: 9, s: 2.8, col: "#fef3c7" }, { r: coreR + 19, dur: 17, s: 2.2, col: "#fbbf24" }].map((p, i) => (
        <Spin key={i} c={c} dur={p.dur} reverse={i % 2 === 1}>
          <circle cx={c + p.r} cy={c} r={p.s} fill={p.col} filter="url(#cbSoft)" />
        </Spin>
      ))}

      {/* core */}
      <circle cx={c} cy={c} r={coreR} fill="url(#cbSunCore)" />
      {/* drifting surface arcs (the "plasma") */}
      <Spin c={c} dur={34}>
        <g opacity={0.5}>
          <path d={`M ${c - coreR * 0.62} ${c - coreR * 0.1} q ${coreR * 0.5} ${-coreR * 0.55} ${coreR * 1.2} ${-coreR * 0.06}`}
            fill="none" stroke="#fff7ed" strokeWidth={2} strokeLinecap="round" filter="url(#cbSoft)" />
          <path d={`M ${c - coreR * 0.5} ${c + coreR * 0.4} q ${coreR * 0.55} ${coreR * 0.35} ${coreR * 1.0} ${coreR * 0.02}`}
            fill="none" stroke="#fed7aa" strokeWidth={1.6} strokeLinecap="round" filter="url(#cbSoft)" />
        </g>
      </Spin>
      <circle cx={c} cy={c} r={coreR} fill="none" stroke="#fff" strokeOpacity={0.25} strokeWidth={1} />

      {label.map((line, i) => (
        <text key={line} x={c} y={c + (i - (label.length - 1) / 2) * 14 + 4} textAnchor="middle"
          fontSize={10.5} fontWeight={800} letterSpacing="0.14em" fill="#fff" style={{ pointerEvents: "none" }}>
          {line}
        </text>
      ))}
    </g>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// AttentionSun — the radial attention map. Severity pulls a node toward the
// sun. Nodes are spaced at even angles with severities interleaved so
// neighbouring planets sit on different orbits, and labels sit radially
// outside their planet with a halo — no overlaps.
// ─────────────────────────────────────────────────────────────────────────────
const SIZE = 720; const C = SIZE / 2;
const R_MIN = 118; const R_MAX = 306;

function AttentionSun({ items, selectedId, onSelect, streaming }) {
  const t = useDashTheme();
  const nodes = React.useMemo(() => {
    const sorted = [...(items || [])].sort((a, b) => b.severity - a.severity);
    // Interleave high/low severities so adjacent angles get different radii —
    // this is what keeps labels from colliding.
    const order = [];
    const half = Math.ceil(sorted.length / 2);
    for (let i = 0; i < half; i++) {
      order.push(sorted[i]);
      if (sorted[i + half]) order.push(sorted[i + half]);
    }
    const n = order.length || 1;
    return order.map((it, i) => {
      const angle = ((i * (360 / n) - 90 + 360 / n / 2) * Math.PI) / 180;
      const r = R_MIN + (1 - it.severity / 100) * (R_MAX - R_MIN);
      const size = 15 + Math.min(11, (it.affected?.length || 1) * 2.2);
      const x = C + r * Math.cos(angle);
      const y = C + r * Math.sin(angle);
      // Label anchored radially outward from the planet
      const lr = r + size + 14;
      const lx = C + lr * Math.cos(angle);
      const ly = C + lr * Math.sin(angle);
      const cos = Math.cos(angle);
      return {
        ...it, x, y, size, lx, ly,
        anchor: cos > 0.35 ? "start" : cos < -0.35 ? "end" : "middle",
        dy: Math.sin(angle) > 0.4 ? 10 : Math.sin(angle) < -0.4 ? -4 : 4,
      };
    });
  }, [items]);

  return (
    <svg viewBox={`0 0 ${SIZE} ${SIZE}`} style={{ width: "100%", height: "auto", display: "block" }}>
      <SunDefs />

      {/* deep-space backdrop */}
      <circle cx={C} cy={C} r={R_MAX + 34} fill="url(#cbSunGlow)" opacity={0.16} />

      {/* orbit rings — slow drift */}
      {[R_MIN, (R_MIN + R_MAX) / 2, R_MAX].map((r, i) => (
        <Spin key={r} c={C} dur={160 + i * 60} reverse={i % 2 === 1}>
          <circle cx={C} cy={C} r={r} fill="none" stroke={t.c.border} strokeWidth={1}
            strokeDasharray="3 7" opacity={0.9 - i * 0.22} />
        </Spin>
      ))}
      {/* ring captions pinned bottom, out of the planets' way */}
      <text x={C} y={C + R_MIN - 10} textAnchor="middle" fontSize={9.5} fontWeight={600}
        fill="#ef4444" opacity={0.75} letterSpacing="0.1em">ACT NOW</text>
      <text x={C} y={C + R_MAX + 22} textAnchor="middle" fontSize={9.5} fontWeight={600}
        fill={t.c.textTertiary} letterSpacing="0.1em">MONITOR</text>

      {/* connector lines — drawn before the core so they stay behind it */}
      {nodes.map((n) => (
        <line key={`ln-${n.id}`} x1={C} y1={C} x2={n.x} y2={n.y} stroke={sevColor(n.severity)}
          strokeWidth={n.id === selectedId ? 1.6 : 0.8} opacity={n.id === selectedId ? 0.6 : 0.16} />
      ))}

      <SunCore c={C} streaming={streaming} />

      {/* planets */}
      {nodes.map((n, i) => {
        const color = sevColor(n.severity);
        const isSel = n.id === selectedId;
        const title = n.title.length > 30 ? n.title.slice(0, 29) + "…" : n.title;
        return (
          <g key={n.id} onClick={() => onSelect(isSel ? null : n.id)} style={{ cursor: "pointer" }}>
            {/* pulse halo for critical planets */}
            {n.severity >= 85 && (
              <circle cx={n.x} cy={n.y} r={n.size + 4} fill="none" stroke={color} strokeWidth={1.4}>
                <animate attributeName="r" values={`${n.size + 3};${n.size + 13};${n.size + 3}`} dur="2s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="0.7;0;0.7" dur="2s" repeatCount="indefinite" />
              </circle>
            )}
            <motion.circle
              cx={n.x} cy={n.y} r={n.size} fill={color}
              initial={{ opacity: 0 }} animate={{ opacity: isSel ? 1 : 0.92 }}
              transition={{ delay: i * 0.06, duration: 0.4 }}
              style={{ filter: isSel ? `drop-shadow(0 0 12px ${color})` : `drop-shadow(0 2px 6px ${color}55)` }}
            />
            <circle cx={n.x - n.size * 0.28} cy={n.y - n.size * 0.3} r={n.size * 0.34} fill="#fff" opacity={0.22} />
            {isSel && <circle cx={n.x} cy={n.y} r={n.size + 6} fill="none" stroke={color} strokeWidth={1.8} opacity={0.75} />}
            <text x={n.x} y={n.y + 4} textAnchor="middle" fontSize={10.5} fontWeight={800} fill="#fff" pointerEvents="none">
              {n.severity}
            </text>
            <text x={n.lx} y={n.ly + n.dy} textAnchor={n.anchor} fontSize={10} fontWeight={650}
              fill={isSel ? t.c.text : t.c.textSecondary} pointerEvents="none"
              stroke={t.c.surface} strokeWidth={3.5} paintOrder="stroke" strokeLinejoin="round">
              {title}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// HeroSun — landing state. The company core is the first thing you see:
// animated sun with ghost planets drifting on their orbits + quick actions.
// ─────────────────────────────────────────────────────────────────────────────
const HERO_GHOSTS = [
  { orbit: 108, size: 10, dur: 22, start: 40, color: "#ef4444" },
  { orbit: 150, size: 8, dur: 34, start: 160, color: "#f97316" },
  { orbit: 150, size: 6, dur: 34, start: 300, color: "#eab308" },
  { orbit: 192, size: 7, dur: 48, start: 220, color: "#10b981" },
];

function HeroSun({ onAnalyze }) {
  const t = useDashTheme();
  const S = 440; const c = S / 2;
  return (
    <div style={{
      position: "relative", borderRadius: t.r.xl, border: `1px solid ${t.c.border}`,
      background: `radial-gradient(ellipse 60% 55% at 50% 38%, ${t.c.surfaceMuted} 0%, ${t.c.surface} 70%)`,
      overflow: "hidden", display: "flex", flexDirection: "column", alignItems: "center",
      padding: `${t.s[5]}px ${t.s[5]}px ${t.s[7]}px`,
    }}>
      {/* star field */}
      {[...Array(18)].map((_, i) => (
        <motion.span key={i}
          animate={{ opacity: [0.1, 0.7, 0.1] }}
          transition={{ duration: 2 + (i % 5), repeat: Infinity, delay: i * 0.35 }}
          style={{
            position: "absolute", width: 2, height: 2, borderRadius: 99, background: t.c.textTertiary,
            left: `${(i * 53) % 96 + 2}%`, top: `${(i * 37) % 88 + 4}%`, pointerEvents: "none",
          }} />
      ))}

      <svg viewBox={`0 0 ${S} ${S}`} style={{ width: "min(430px, 92%)", height: "auto", display: "block" }}>
        <SunDefs />
        {HERO_GHOSTS.map((g, i) => (
          <g key={i}>
            <circle cx={c} cy={c} r={g.orbit} fill="none" stroke={t.c.border} strokeWidth={1} strokeDasharray="3 7" opacity={0.75} />
            {/* static offset sets the planet's start position; SMIL spins the group */}
            <g transform={`rotate(${g.start} ${c} ${c})`}>
              <Spin c={c} dur={g.dur} reverse={i % 2 === 1}>
                <circle cx={c + g.orbit} cy={c} r={g.size} fill={g.color} opacity={0.9}
                  style={{ filter: `drop-shadow(0 0 6px ${g.color}88)` }} />
              </Spin>
            </g>
          </g>
        ))}
        <SunCore c={c} coreR={56} />
      </svg>

      <div style={{ textAlign: "center", maxWidth: 520, marginTop: -6, position: "relative" }}>
        <div style={{ fontSize: 20, fontWeight: 750, letterSpacing: "-0.02em", color: t.c.text }}>
          See what orbits your company core
        </div>
        <p style={{ fontSize: t.font.size.sm, color: t.c.textSecondary, lineHeight: 1.6, margin: `${t.s[2]}px 0 0` }}>
          Cross-team evidence is mined into an attention map — risks, blind spots, repeated manual
          workflows and growth patterns. The closer a planet orbits the sun, the more urgent it is.
        </p>
        <div style={{ display: "flex", gap: t.s[2], justifyContent: "center", marginTop: t.s[5], flexWrap: "wrap" }}>
          <Button variant="primary" leftIcon={<Zap size={15} />} onClick={() => onAnalyze("7d")}>
            Analyze last 7 days
          </Button>
          <Button variant="secondary" onClick={() => onAnalyze("30d")}>Last 30 days</Button>
          <Button variant="secondary" onClick={() => onAnalyze(null)}>Use current filters</Button>
        </div>
        <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, marginTop: t.s[3] }}>
          Scope with the filters above — entire company, one person, or a person plus their team. Reports are cached.
        </div>
      </div>
    </div>
  );
}

// Large professional detail panel for the selected sun node.
function AttentionDetail({ item, onClose }) {
  const t = useDashTheme();
  if (!item) {
    return (
      <div style={{ padding: t.s[5], display: "flex", flexDirection: "column", gap: t.s[3], color: t.c.textTertiary, fontSize: t.font.size.sm }}>
        <div style={{ fontSize: t.font.size.md, fontWeight: 650, color: t.c.textSecondary }}>How to read the map</div>
        <div>Every planet is a pattern found in your company's tracked work. The closer it orbits the sun, the more urgent it is.</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {[[85, "Critical — act this week"], [60, "High — handle this month"], [30, "Monitor"], [10, "Minor / positive"]].map(([s, l]) => (
            <span key={s} style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <span style={{ width: 10, height: 10, borderRadius: 99, background: sevColor(s), flexShrink: 0 }} />{l}
            </span>
          ))}
        </div>
        <div>Click any planet to see the full problem statement, evidence and the exact next step.</div>
      </div>
    );
  }
  const color = sevColor(item.severity);
  const CatIcon = CATEGORY_ICONS[item.category] || Target;
  return (
    <motion.div key={item.id} initial={{ opacity: 0, x: 14 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.25 }}
      style={{ padding: t.s[4], display: "flex", flexDirection: "column", gap: t.s[3] }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 30, height: 30, borderRadius: t.r.md, background: color + "1c", color }}>
          <CatIcon size={15} />
        </span>
        <Badge tone="neutral">{item.category}</Badge>
        <Badge tone={item.severity >= 60 ? "warning" : "neutral"}>{sevLabel(item.severity)} · {item.severity}</Badge>
        <button type="button" onClick={onClose} aria-label="Close details" style={{
          marginLeft: "auto", border: "none", background: "transparent", cursor: "pointer",
          color: t.c.textTertiary, fontSize: t.font.size.xs,
        }}>✕</button>
      </div>

      <div style={{ fontSize: t.font.size.xl, fontWeight: 700, color: t.c.text, letterSpacing: "-0.015em", lineHeight: 1.25 }}>
        {item.title}
      </div>

      {/* severity meter */}
      <div style={{ height: 6, borderRadius: 99, background: t.c.surfaceMuted, overflow: "hidden" }}>
        <motion.div initial={{ width: 0 }} animate={{ width: `${item.severity}%` }} transition={{ duration: 0.5 }}
          style={{ height: "100%", borderRadius: 99, background: color }} />
      </div>

      {item.affected?.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          {item.affected.slice(0, 8).map((n) => (
            <span key={n} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: t.font.size.xs, color: t.c.textSecondary, padding: "2px 8px 2px 3px", borderRadius: 99, border: `1px solid ${t.c.border}` }}>
              <Avatar name={n} size={16} />{n}
            </span>
          ))}
        </div>
      )}

      <p style={{ fontSize: t.font.size.sm, color: t.c.textSecondary, lineHeight: 1.6, margin: 0 }}>{item.problem}</p>

      {item.evidence?.length > 0 && (
        <div>
          <div style={{ fontSize: 10.5, fontWeight: 700, color: t.c.textTertiary, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>Evidence</div>
          {item.evidence.map((e, i) => (
            <div key={i} style={{ display: "flex", gap: 8, fontSize: t.font.size.xs, color: t.c.textSecondary, lineHeight: 1.55, padding: "3px 0" }}>
              <span style={{ width: 5, height: 5, borderRadius: 99, background: color, flexShrink: 0, marginTop: 6 }} />{e}
            </div>
          ))}
        </div>
      )}

      {item.recommendation && (
        <div style={{ borderRadius: t.r.md, border: `1px solid ${color}44`, background: color + "0d", padding: t.s[3] }}>
          <div style={{ fontSize: 10.5, fontWeight: 700, color, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4, display: "inline-flex", alignItems: "center", gap: 5 }}>
            <ChevronRight size={11} />Next step · {item.effort} effort
          </div>
          <div style={{ fontSize: t.font.size.sm, color: t.c.text, lineHeight: 1.55 }}>{item.recommendation}</div>
        </div>
      )}
    </motion.div>
  );
}

// KPI tile (compact, same visual language as Work Intelligence).
function Kpi({ label, value, suffix, icon, accent }) {
  const t = useDashTheme();
  return (
    <div style={{ padding: t.s[3], borderRadius: t.r.md, border: `1px solid ${t.c.border}`, background: t.c.surface, display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 10.5, color: t.c.textTertiary, fontWeight: 500, whiteSpace: "nowrap" }}>
        <span style={{ display: "inline-flex", color: accent || t.c.icon }}>{icon}</span>{label}
      </span>
      <span style={{ fontSize: 19, fontWeight: 700, color: t.c.text, letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums" }}>
        {value ?? "—"}<span style={{ fontSize: t.font.size.xs, fontWeight: 500, color: t.c.textTertiary, marginLeft: 2 }}>{value != null ? suffix : ""}</span>
      </span>
    </div>
  );
}

function ChartTip({ active, payload, label, suffix = "" }) {
  const t = useDashTheme();
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: t.c.surface, border: `1px solid ${t.c.border}`, borderRadius: t.r.md, boxShadow: t.shadow.lg, padding: "6px 10px", fontSize: t.font.size.xs }}>
      <div style={{ color: t.c.textTertiary, marginBottom: 2 }}>{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} style={{ color: t.c.text, fontWeight: 600 }}>{p.name || p.dataKey}: {p.value}{suffix}</div>
      ))}
    </div>
  );
}

// Pattern card: kicker row → full-width title → description → people + meta.
// (The title never fights the meta badge for width.)
function PatternCard({ color, icon, kicker, title, description, users, meta }) {
  const t = useDashTheme();
  return (
    <div style={{
      borderRadius: t.r.lg, border: `1px solid ${t.c.border}`, background: t.c.surface,
      padding: t.s[4], display: "flex", flexDirection: "column", gap: t.s[2],
      boxShadow: t.shadow.sm, position: "relative", overflow: "hidden",
    }}>
      <span style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 3, background: color }} />
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 26, height: 26, borderRadius: t.r.sm, background: color + "16", color, flexShrink: 0 }}>
          {icon}
        </span>
        <span style={{ fontSize: 10, fontWeight: 750, color, textTransform: "uppercase", letterSpacing: "0.09em" }}>{kicker}</span>
      </div>
      <div style={{ fontSize: t.font.size.md, fontWeight: 700, color: t.c.text, letterSpacing: "-0.01em", lineHeight: 1.35 }}>
        {title}
      </div>
      <p style={{ fontSize: t.font.size.sm, color: t.c.textSecondary, lineHeight: 1.6, margin: 0, flex: 1 }}>{description}</p>
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginTop: 2 }}>
        {(users || []).slice(0, 5).map((n) => (
          <span key={n} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 10.5, padding: "2px 8px 2px 3px", borderRadius: 99, background: t.c.surfaceMuted, color: t.c.textSecondary, border: `1px solid ${t.c.border}` }}>
            <Avatar name={n} size={15} />{n}
          </span>
        ))}
        {meta && <span style={{ marginLeft: "auto", fontSize: 10.5, color: t.c.textTertiary, fontWeight: 600 }}>{meta}</span>}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

export default function CompanyBrainV2() {
  const t = useDashTheme();
  const { user } = useAuth();
  const stream = useAiStream("/api/v1/company-brain/metrics-stream");

  const [member, setMember] = React.useState(null); // null = entire company
  const [includeTeam, setIncludeTeam] = React.useState(true);
  const [range, setRange] = React.useState(() => presetToRange("7d"));
  const [accessibleUsers, setAccessibleUsers] = React.useState([]);
  const [loadingUsers, setLoadingUsers] = React.useState(true);
  const [selectedId, setSelectedId] = React.useState(null);
  const [started, setStarted] = React.useState(false);

  React.useEffect(() => {
    (async () => {
      setLoadingUsers(true);
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${SERVER_URL}/api/v1/saved-summaries?limit=1`, { headers: { Authorization: `Bearer ${token}` } });
        const json = await res.json();
        if (json?.success && json.data?.accessibleUsers) setAccessibleUsers(json.data.accessibleUsers);
      } catch (_) {} finally { setLoadingUsers(false); }
    })();
  }, [user]);

  const members = React.useMemo(() => [
    { value: null, label: "Entire company" },
    ...accessibleUsers.map((u) => ({
      value: u._id || u.id,
      label: `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email || "Member",
    })),
  ], [accessibleUsers]);

  // presetKey (from a hero button) overrides the toolbar range; null = keep it.
  const analyze = React.useCallback((presetKey = null, force = false) => {
    const r = presetKey ? rangeForPreset(presetKey) : range;
    if (presetKey) setRange(r);
    setStarted(true);
    setSelectedId(null);
    stream.run({
      userId: member || null,
      includeTeam: member ? includeTeam : false,
      startDate: fmt(r.start),
      endDate: fmt(r.end),
      forceRegenerate: force,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member, includeTeam, range]);

  const m = stream.metrics;
  const report = stream.report;
  const attention = report?.attention || [];
  const selected = attention.find((a) => a.id === selectedId) || null;
  const axisTick = { fontSize: 9.5, fill: t.c.textTertiary };
  const health = report?.healthScore;
  const healthColor = health == null ? t.c.textTertiary : health >= 70 ? "#10b981" : health >= 45 ? "#f59e0b" : "#ef4444";

  return (
    <PageShell
      eyebrow="AI · Organization"
      title="Org Intelligence"
      subtitle="Cross-team patterns, risks and growth signals — what needs your attention"
      icon={<BrainCircuit size={22} />}
      topRight={
        <div className="tg-filterbar">
          <ToolSelect
            icon={<Users size={13} />}
            value={member}
            options={members}
            onChange={(v) => { setMember(v ?? null); }}
            placeholder={loadingUsers ? "Loading…" : "Entire company"}
            panelWidth={250}
            placement="bottomRight"
          />
          {member && (
            <>
              <span className="tg-tool-sep" />
              <button type="button" className="tg-tool" onClick={() => setIncludeTeam((x) => !x)}
                style={includeTeam ? { color: t.c.primary } : undefined}>
                <Check size={13} style={{ opacity: includeTeam ? 1 : 0.25 }} />
                <span className="tg-tool-value">Include team</span>
              </button>
            </>
          )}
          <span className="tg-tool-sep" />
          <DateRangeTool range={range} onChange={setRange} layoutId="companyBrainRangeChip"
            presets={[
              { key: "7d", label: "7 days" },
              { key: "30d", label: "30 days" },
              { key: "month", label: "This month" },
              { key: "lastMonth", label: "Last month" },
            ]}
          />
        </div>
      }
      actions={
        stream.streaming ? (
          <Button variant="secondary" leftIcon={<Square size={14} />} onClick={stream.stop}>Stop</Button>
        ) : (
          <Button variant="primary" leftIcon={<Zap size={15} />} onClick={() => analyze(null)}>Analyze</Button>
        )
      }
    >
      {!started ? (
        <HeroSun onAnalyze={(preset) => analyze(preset)} />
      ) : (
        <>
          {/* ── Headline + health ── */}
          <div style={{
            display: "flex", alignItems: "center", gap: 14, marginBottom: t.s[4],
            padding: `${t.s[4]}px ${t.s[5]}px`, borderRadius: t.r.lg,
            border: `1px solid ${t.c.border}`, background: t.c.surface,
          }}>
            <div style={{ position: "relative", width: 52, height: 52, flexShrink: 0 }}>
              <svg viewBox="0 0 52 52" style={{ width: 52, height: 52, transform: "rotate(-90deg)" }}>
                <circle cx="26" cy="26" r="22" fill="none" stroke={t.c.surfaceMuted} strokeWidth="5" />
                {health != null && (
                  <motion.circle cx="26" cy="26" r="22" fill="none" stroke={healthColor} strokeWidth="5" strokeLinecap="round"
                    strokeDasharray={`${(health / 100) * 138} 138`} initial={{ strokeDasharray: "0 138" }}
                    animate={{ strokeDasharray: `${(health / 100) * 138} 138` }} transition={{ duration: 0.8 }} />
                )}
              </svg>
              <span style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, color: healthColor, fontVariantNumeric: "tabular-nums" }}>
                {health ?? "…"}
              </span>
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: t.font.size.lg, fontWeight: 650, color: t.c.text, letterSpacing: "-0.01em", lineHeight: 1.35 }}>
                {report?.headline || (stream.streaming ? "Reading cross-team evidence…" : "Run an analysis to see the company read")}
              </div>
              {report?.summary && <div style={{ fontSize: t.font.size.sm, color: t.c.textSecondary, marginTop: 2, lineHeight: 1.5 }}>{report.summary}</div>}
            </div>
            {stream.streaming && (
              <motion.span animate={{ rotate: 360 }} transition={{ duration: 1.1, repeat: Infinity, ease: "linear" }}
                style={{ display: "inline-flex", color: t.c.primary, flexShrink: 0 }}><Sparkles size={18} /></motion.span>
            )}
          </div>

          {/* ── KPI row ── */}
          {m && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: t.s[2], marginBottom: t.s[4] }}>
              <Kpi label="Scope" value={m.scope?.people} suffix=" people" icon={<Users size={11} />} accent={t.accents?.[0]} />
              <Kpi label="Coverage" value={m.kpis?.coveragePct} suffix="%" icon={<Target size={11} />} accent={t.accents?.[1]} />
              <Kpi label="Tracked hours" value={m.kpis?.totalHours} suffix="h" icon={<Clock size={11} />} accent={t.accents?.[2]} />
              <Kpi label="Blind spots" value={m.kpis?.blindSpots} icon={<EyeOff size={11} />} accent="#ef4444" />
              <Kpi label="Attention items" value={report ? attention.length : null} icon={<AlertCircle size={11} />} accent="#f97316" />
            </div>
          )}

          {stream.error && !report && (
            <EmptyState
              icon={<AlertCircle size={22} />}
              title={/no tracked activity/i.test(stream.error || "") ? "No activity found" : "Analysis unavailable"}
              description={(stream.error || "").replace(/\*\*/g, "")}
              action={!/no tracked activity/i.test(stream.error || "")
                ? <Button variant="primary" leftIcon={<Zap size={15} />} onClick={() => analyze(null)}>Retry</Button>
                : null}
            />
          )}

          {/* ── The attention map ── */}
          {(attention.length > 0 || stream.streaming) && (
            <SectionCard
              title="Attention map"
              subtitle="Every planet is a detected pattern — the closer to the sun, the more urgent. Click to inspect."
              icon={<BrainCircuit size={15} />}
            >
              <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 3fr) minmax(280px, 2fr)", gap: t.s[3], padding: t.s[3], alignItems: "start" }}>
                <div style={{ minWidth: 0 }}>
                  {attention.length > 0 ? (
                    <AttentionSun items={attention} selectedId={selectedId} onSelect={setSelectedId} streaming={stream.streaming} />
                  ) : (
                    <div style={{ position: "relative", minHeight: 380 }}>
                      <svg viewBox="0 0 400 300" style={{ width: "100%", height: "auto" }}>
                        <SunDefs />
                        <SunCore c={200} coreR={44} streaming label={["SCANNING"]} />
                      </svg>
                      <div style={{ textAlign: "center", marginTop: -8 }}>
                        <div style={{ fontSize: t.font.size.md, fontWeight: 600, color: t.c.text }}>Mapping company patterns…</div>
                        <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, marginTop: 2 }}>Mining cross-team evidence and ranking what needs attention</div>
                      </div>
                    </div>
                  )}
                </div>
                <div style={{ borderLeft: `1px solid ${t.c.border}`, minHeight: 340, position: "sticky", top: 0 }}>
                  <AnimatePresence mode="wait">
                    <AttentionDetail item={selected} onClose={() => setSelectedId(null)} />
                  </AnimatePresence>
                </div>
              </div>
            </SectionCard>
          )}

          {/* ── Team analytics ── */}
          {m && (
            <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 3fr) minmax(0, 2fr)", gap: t.s[4], marginTop: t.s[4], alignItems: "start" }}>
              <SectionCard title="Team activity" subtitle="Tracked evidence volume per person" icon={<Users size={15} />}>
                <div style={{ padding: `${t.s[2]}px ${t.s[3]}px ${t.s[3]}px` }}>
                  <ResponsiveContainer width="100%" height={Math.max(180, (m.teamActivity?.length || 0) * 22)}>
                    <BarChart data={m.teamActivity || []} layout="vertical" margin={{ left: 8, right: 8 }}>
                      <XAxis type="number" hide />
                      <YAxis type="category" dataKey="name" width={86} tick={axisTick} axisLine={false} tickLine={false} />
                      <Tooltip content={<ChartTip />} cursor={{ fill: t.c.surfaceMuted }} />
                      <Bar dataKey="screenshots" name="evidence" radius={[0, 3, 3, 0]}>
                        {(m.teamActivity || []).map((r, i) => (
                          <Cell key={i} fill={r.trendPct != null && r.trendPct <= -30 ? "#f97316" : t.c.primary} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </SectionCard>

              <div style={{ display: "flex", flexDirection: "column", gap: t.s[4], minWidth: 0 }}>
                <SectionCard title="Company rhythm" subtitle="Daily activity across the range" icon={<TrendingUp size={15} />}>
                  <div style={{ padding: `${t.s[2]}px ${t.s[3]}px ${t.s[3]}px` }}>
                    <ResponsiveContainer width="100%" height={120}>
                      <AreaChart data={m.orgTrend || []}>
                        <XAxis dataKey="date" tick={axisTick} axisLine={false} tickLine={false} tickFormatter={(d) => d.slice(5)} />
                        <YAxis hide />
                        <Tooltip content={<ChartTip />} />
                        <Area dataKey="screenshots" name="activity" stroke={t.c.primary} fill={t.c.primary + "33"} strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </SectionCard>

                {(m.blindSpotList?.length > 0 || m.declineList?.length > 0) && (
                  <SectionCard title="Verified signals" subtitle="Computed directly from tracking — not AI" icon={<AlertCircle size={15} />}>
                    <div style={{ padding: t.s[3], display: "flex", flexDirection: "column", gap: 6 }}>
                      {(m.blindSpotList || []).slice(0, 6).map((b) => (
                        <div key={b.name} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: t.font.size.xs }}>
                          <EyeOff size={12} style={{ color: "#ef4444", flexShrink: 0 }} />
                          <span style={{ color: t.c.text, fontWeight: 600 }}>{b.name}</span>
                          <span style={{ color: t.c.textTertiary }}>{b.position}</span>
                          <span style={{ marginLeft: "auto", color: "#ef4444", fontWeight: 600 }}>no data</span>
                        </div>
                      ))}
                      {(m.declineList || []).map((d) => (
                        <div key={d.name} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: t.font.size.xs }}>
                          <TrendingDown size={12} style={{ color: "#f97316", flexShrink: 0 }} />
                          <span style={{ color: t.c.text, fontWeight: 600 }}>{d.name}</span>
                          <span style={{ marginLeft: "auto", color: "#f97316", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{d.pct}%</span>
                        </div>
                      ))}
                    </div>
                  </SectionCard>
                )}
              </div>
            </div>
          )}

          {/* ── Automations ── */}
          {report?.automations?.length > 0 && (
            <div style={{ marginTop: t.s[4] }}>
              <SectionCard title="Automation opportunities" subtitle="Repeated manual workflows observed in the evidence" icon={<Workflow size={15} />}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: t.s[3], padding: t.s[3] }}>
                  {report.automations.map((a, i) => (
                    <PatternCard key={i} color="#8b5cf6" icon={<Workflow size={14} />} kicker="Automation candidate"
                      title={a.title} description={a.description} users={a.users}
                      meta={[a.frequency, a.tool].filter(Boolean).join(" · ")} />
                  ))}
                </div>
              </SectionCard>
            </div>
          )}

          {/* ── Growth patterns ── */}
          {report?.growth?.length > 0 && (
            <div style={{ marginTop: t.s[4] }}>
              <SectionCard title="Growth patterns" subtitle="What's working — and worth scaling" icon={<Rocket size={15} />}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: t.s[3], padding: t.s[3] }}>
                  {report.growth.map((g, i) => (
                    <PatternCard key={i} color="#10b981" icon={<Rocket size={14} />} kicker="Scale this"
                      title={g.title} description={g.description} users={g.users} />
                  ))}
                </div>
              </SectionCard>
            </div>
          )}

          {/* ── Communication ── */}
          {report?.communication?.summary && (
            <div style={{ marginTop: t.s[4] }}>
              <SectionCard title="Communication flow" icon={<MessageSquare size={15} />}>
                <div style={{ padding: t.s[4], display: "flex", flexDirection: "column", gap: t.s[3] }}>
                  <p style={{ fontSize: t.font.size.sm, color: t.c.textSecondary, lineHeight: 1.6, margin: 0 }}>{report.communication.summary}</p>
                  {report.communication.channels?.length > 0 && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {(() => {
                        const max = Math.max(...report.communication.channels.map((c) => c.weight), 1);
                        return report.communication.channels.map((c) => (
                          <div key={c.name} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ width: 110, fontSize: t.font.size.xs, color: t.c.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</span>
                            <div style={{ flex: 1, height: 6, borderRadius: 99, background: t.c.surfaceMuted, overflow: "hidden" }}>
                              <div style={{ width: `${Math.round((c.weight / max) * 100)}%`, height: "100%", borderRadius: 99, background: t.accents?.[3] || t.c.primary }} />
                            </div>
                          </div>
                        ));
                      })()}
                    </div>
                  )}
                </div>
              </SectionCard>
            </div>
          )}
        </>
      )}

      <AILimitModal isOpen={stream.limitReached} onClose={() => stream.setLimitReached(false)} />
    </PageShell>
  );
}
